import { ExtensionType, GeneratedAnimalName } from '../types/ExtensionCard';
import { GameStage, HandType } from '../types/roomState';
import { Locale } from './types';

type ExtensionTexts = {
  name: string;
  description: string;
  shortName?: string;
};

const CARD_TEXTS: Record<Locale, Record<ExtensionType, ExtensionTexts>> = {
  en: {
    [ExtensionType.predator]: {
      name: 'Predator',
      description: 'Can attack other animals',
    },
    [ExtensionType.chlorophyll]: {
      name: 'Photosynthesis',
      description:
        'Needs less food. Cannot be a predator and cannot cooperate.',
    },
    [ExtensionType.aedificator]: {
      name: 'Aedificator',
      shortName: 'Aed.',
      description: 'Adds two food to the food base at the start of the turn',
    },
    [ExtensionType.horns]: {
      name: 'Horns',
      description: 'After an attack, has a 60% chance to kill the predator',
    },
    [ExtensionType.transparent]: {
      name: 'Transparency',
      shortName: 'Transp.',
      description: 'Cannot be attacked before it has eaten',
    },
    [ExtensionType.camouflage]: {
      name: 'Camouflage',
      description: 'Can only be attacked by a predator with sharp vision',
    },
    [ExtensionType.sharpVision]: {
      name: 'Sharp Vision',
      shortName: 'Sharp V.',
      description: 'Can attack animals with camouflage',
    },
    [ExtensionType.big]: {
      name: 'Large',
      description: 'Can only be attacked by a large predator',
    },
    [ExtensionType.burrowing]: {
      name: 'Burrowing',
      description: 'When fed, cannot be attacked by a predator',
    },
    [ExtensionType.crazing]: {
      name: 'Trampling',
      description: 'When used, destroys one food from the food base',
    },
    [ExtensionType.hibernation]: {
      name: 'Hibernation',
      description:
        'After use, counts as fed. Cannot be used two turns in a row.',
    },
    [ExtensionType.poisonous]: {
      name: 'Poisonous',
      shortName: 'Pois.',
      description: 'The predator that eats this animal dies during extinction',
    },
    [ExtensionType.running]: {
      name: 'Running',
      description:
        'When attacked, has a chance not to be eaten (50% against a normal predator, 10% against a running one)',
    },
    [ExtensionType.scavenger]: {
      name: 'Scavenger',
      description: 'Gets one food when another animal is eaten',
    },
    [ExtensionType.swimming]: {
      name: 'Swimming',
      shortName: 'Swim.',
      description: 'Can only be attacked by a swimming predator',
    },
    [ExtensionType.tailLoss]: {
      name: 'Tail Loss',
      shortName: 'Tail L.',
      description:
        'When attacked, discards this or another random evolution card',
    },
    [ExtensionType.piracy]: {
      name: 'Piracy',
      description: 'Take food from another unfed animal',
    },
    [ExtensionType.parasite]: {
      name: 'Parasite',
      description: "Can be added to another player's animal",
    },
    [ExtensionType.scream]: {
      name: 'Scream',
      description: 'Warns adjacent animals when attacked',
    },
    [ExtensionType.spikes]: {
      name: 'Spikes',
      description:
        'After attacking this animal, the predator cannot attack again this turn',
    },
    [ExtensionType.parasitePair]: {
      name: 'Trematode',
      description: "Can be added to a pair of another player's animals",
    },
    [ExtensionType.fat]: {
      name: 'Fat Reserve',
      shortName: 'Fat Res.',
      description: 'Consumes stored energy for feeding when used',
    },
    [ExtensionType.cooperation]: {
      name: 'Cooperation',
      shortName: 'Coop.',
      description:
        'When one animal in the pair gets food, the other gets food too',
    },
    [ExtensionType.communication]: {
      name: 'Communication',
      shortName: 'Comm.',
      description:
        'When one animal in the pair eats from the food base, the other gets food out of turn',
    },
    [ExtensionType.fly]: {
      name: 'Flight',
      description: 'Cannot be attacked by a "heavier" predator',
    },
    [ExtensionType.angler]: {
      name: 'Angler',
      description:
        'While undisclosed and the only evolution card, counterattacks the predator.',
    },
    [ExtensionType.pseudoPoisonous]: {
      name: 'Bright Coloration',
      shortName: 'Bright.',
      description: 'Looks like Poisonous. So does the card.',
    },
    [ExtensionType.egg]: {
      name: 'Egg',
      description: 'This animal has not hatched yet. It cannot do anything.',
    },
    [ExtensionType.layingEggs]: {
      name: 'Egg Laying',
      shortName: 'Egg L.',
      description: 'When fed, lays several eggs. They hatch after a few turns',
    },
    [ExtensionType.viviparous]: {
      name: 'Viviparity',
      shortName: 'Viv.',
      description:
        'When fed, gives birth to a young animal. The offspring may inherit evolution cards.',
    },
    [ExtensionType.ink]: {
      name: 'Ink Cloud',
      shortName: 'Ink Cl.',
      description: 'Interrupts an attack on itself once per turn',
    },
    [ExtensionType.patron]: {
      name: 'Patron',
      description: 'Protects adjacent animals without evolution cards',
    },
    [ExtensionType.immunity]: {
      name: 'Immunity',
      description:
        'Parasites cannot be placed on this animal. Existing parasites are removed',
    },
    [ExtensionType.intellect]: {
      name: 'Intellect',
      shortName: '🧠',
      description: 'Once per turn ignores one defense card of another animal',
    },
    [ExtensionType.tumor]: {
      name: 'Tumor',
      description: 'Gradually kills the animal',
    },
    [ExtensionType.shell]: {
      name: 'Shell',
      description:
        'During an attack, hides in its shell and cannot eat until the end of the turn.',
    },
  },
  ru: {
    [ExtensionType.predator]: {
      name: 'Хищник',
      description: 'Может атаковать других животных',
    },
    [ExtensionType.chlorophyll]: {
      name: 'Фотосинтез',
      description:
        'Требует меньше еды. Не может быть хищником и не сочетается с парными свойствами.',
    },
    [ExtensionType.aedificator]: {
      name: 'Эдификатор',
      shortName: 'Эдиф.',
      description: 'Добавляет 2 еды в общую кормовую базу в начале хода',
    },
    [ExtensionType.horns]: {
      name: 'Рога',
      description: 'После атаки с шансом 60% убивает хищника',
    },
    [ExtensionType.transparent]: {
      name: 'Прозрачность',
      shortName: 'Прозр.',
      description: 'Нельзя атаковать, пока животное ещё не поело',
    },
    [ExtensionType.camouflage]: {
      name: 'Камуфляж',
      description: 'Можно атаковать только хищником с острым зрением',
    },
    [ExtensionType.sharpVision]: {
      name: 'Острое зрение',
      shortName: 'Остр. зр.',
      description: 'Позволяет атаковать животных с камуфляжем',
    },
    [ExtensionType.big]: {
      name: 'Крупное',
      description: 'Может быть атаковано только крупным хищником',
    },
    [ExtensionType.burrowing]: {
      name: 'Норное',
      description: 'После насыщения не может быть атаковано хищником',
    },
    [ExtensionType.crazing]: {
      name: 'Вытаптывание',
      description: 'При использовании уничтожает 1 еду из кормовой базы',
    },
    [ExtensionType.hibernation]: {
      name: 'Спячка',
      description:
        'После использования считается накормленным. Нельзя использовать два хода подряд.',
    },
    [ExtensionType.poisonous]: {
      name: 'Ядовитое',
      shortName: 'Яд.',
      description: 'Хищник, съевший это животное, погибает на вымирании',
    },
    [ExtensionType.running]: {
      name: 'Бег',
      description:
        'При атаке может спастись бегством (50% против обычного хищника, 10% против хищника с бегом)',
    },
    [ExtensionType.scavenger]: {
      name: 'Падальщик',
      description: 'Получает 1 еду, когда съедают другое животное',
    },
    [ExtensionType.swimming]: {
      name: 'Водоплавающее',
      shortName: 'Вода',
      description: 'Может быть атаковано только водоплавающим хищником',
    },
    [ExtensionType.tailLoss]: {
      name: 'Отбрасывание хвоста',
      shortName: 'Хвост',
      description: 'При атаке сбрасывает это или другое случайное свойство',
    },
    [ExtensionType.piracy]: {
      name: 'Пиратство',
      description: 'Забирает еду у другого ненакормленного животного',
    },
    [ExtensionType.parasite]: {
      name: 'Паразит',
      description: 'Может быть выложен на животное другого игрока',
    },
    [ExtensionType.scream]: {
      name: 'Крик',
      description: 'Предупреждает соседних животных при атаке',
    },
    [ExtensionType.spikes]: {
      name: 'Шипы',
      description:
        'После атаки этого животного хищник не может атаковать снова в этом ходу',
    },
    [ExtensionType.parasitePair]: {
      name: 'Трематода',
      description: 'Может быть выложена на пару животных другого игрока',
    },
    [ExtensionType.fat]: {
      name: 'Жировой запас',
      shortName: 'Жир',
      description:
        'При использовании расходует запасённую энергию на кормление',
    },
    [ExtensionType.cooperation]: {
      name: 'Сотрудничество',
      shortName: 'Сотр.',
      description:
        'Когда одно животное в паре получает еду, второе тоже получает еду',
    },
    [ExtensionType.communication]: {
      name: 'Коммуникация',
      shortName: 'Комм.',
      description:
        'Когда одно животное в паре ест из кормовой базы, второе получает еду вне очереди',
    },
    [ExtensionType.fly]: {
      name: 'Полёт',
      description: 'Не может быть атаковано более «лёгким» хищником',
    },
    [ExtensionType.angler]: {
      name: 'Удильщик',
      description:
        'Пока не раскрыт и остаётся единственным свойством, контратакует хищника.',
    },
    [ExtensionType.pseudoPoisonous]: {
      name: 'Яркая окраска',
      shortName: 'Ярк.',
      description: 'Выглядит как Ядовитое. Так же выглядит и сама карта.',
    },
    [ExtensionType.egg]: {
      name: 'Яйцо',
      description:
        'Это животное ещё не вылупилось. Оно ничего не может делать.',
    },
    [ExtensionType.layingEggs]: {
      name: 'Откладывание яиц',
      shortName: 'Яйца',
      description:
        'После насыщения откладывает несколько яиц. Они вылупляются через несколько ходов',
    },
    [ExtensionType.viviparous]: {
      name: 'Живорождение',
      shortName: 'Живо.',
      description:
        'После насыщения рождает молодое животное. Потомок может унаследовать свойства.',
    },
    [ExtensionType.ink]: {
      name: 'Чернильное облако',
      shortName: 'Чернила',
      description: 'Один раз за ход прерывает атаку на себя',
    },
    [ExtensionType.patron]: {
      name: 'Покровитель',
      description: 'Защищает соседних животных без карт эволюции',
    },
    [ExtensionType.immunity]: {
      name: 'Иммунитет',
      description:
        'На это животное нельзя класть паразитов. Уже существующие паразиты удаляются',
    },
    [ExtensionType.intellect]: {
      name: 'Интеллект',
      shortName: '🧠',
      description: 'Один раз за ход игнорирует одну защиту другого животного',
    },
    [ExtensionType.tumor]: {
      name: 'Опухоль',
      description: 'Постепенно убивает животное',
    },
    [ExtensionType.shell]: {
      name: 'Панцирь',
      description:
        'Во время атаки прячется в панцирь и не может есть до конца хода.',
    },
  },
};

const STAGE_LABELS: Record<Locale, Record<GameStage, string>> = {
  en: {
    [GameStage.placing]: 'Card Placement',
    [GameStage.eating]: 'Feeding',
    [GameStage.extinction]: 'Extinction',
    [GameStage.end]: 'Results',
  },
  ru: {
    [GameStage.placing]: 'Розыгрыш карт',
    [GameStage.eating]: 'Кормление',
    [GameStage.extinction]: 'Вымирание',
    [GameStage.end]: 'Результаты',
  },
};

const HAND_TYPE_LABELS: Record<Locale, Record<HandType, string>> = {
  en: {
    [HandType.human]: 'Human',
    [HandType.poc]: 'POC',
    [HandType.stick]: 'Stick',
    [HandType.material]: 'Material',
    [HandType.altushka]: 'Altushka',
    [HandType.cat]: 'Cat',
  },
  ru: {
    [HandType.human]: 'Человек',
    [HandType.poc]: 'POC',
    [HandType.stick]: 'Палка',
    [HandType.material]: 'Материал',
    [HandType.altushka]: 'Альтушка',
    [HandType.cat]: 'Кот',
  },
};

const GENERATED_ANIMAL_NAMES: Record<
  Locale,
  Record<GeneratedAnimalName, string>
> = {
  en: {
    animal: 'Animal',
    fish: 'Fish',
    predator: 'Predator',
    predatorFish: 'Predator Fish',
    egg: 'Egg',
    young: 'Young',
  },
  ru: {
    animal: 'Животное',
    fish: 'Рыба',
    predator: 'Хищник',
    predatorFish: 'Хищная рыба',
    egg: 'Яйцо',
    young: 'Детёныш',
  },
};

export const getExtensionTexts = (
  locale: Locale,
  type: ExtensionType,
): ExtensionTexts => {
  return CARD_TEXTS[locale][type];
};

export const getStageLabel = (locale: Locale, stage: GameStage): string => {
  return STAGE_LABELS[locale][stage];
};

export const getHandTypeLabel = (locale: Locale, type: HandType): string => {
  return HAND_TYPE_LABELS[locale][type];
};

export const getGeneratedAnimalName = (
  locale: Locale,
  name: GeneratedAnimalName,
): string => {
  return GENERATED_ANIMAL_NAMES[locale][name];
};
